import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { SelectedItem } from './AIObjectPicker';

interface AgentChatState {
  messages: ChatMessage[];
  threadId: string | null;
  isLoading?: boolean;
  contextMessage?: string;
}
export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  isHeader?: boolean;
  headerText?: string;
  lookupDocs?: any[];
  sourceIndexes?: string[];
  selectedIndexes?: string[];
  recordsByIndex?: Record<string, number>;
  conversationId?: string;
  reusedDataset?: boolean;
  threadId?: string;
  debug?: any;
  isError?: boolean;
}
interface ChatMessageBasic {
  Role: 'system' | 'user' | 'assistant' | 'divider';
  Content: string;
  History: any[];
}
interface AiContextProps {
  agents: Record<string, AgentChatState>;
  updateAgent: (agentType: string, data: Partial<AgentChatState>) => void;
  getAgent: (agentType: string) => AgentChatState;
  handleSendMessage: (agentType: string, message: string, isHeader: boolean, headerText: string, bryntumJson?: any) => Promise<void>;
  handleSendLookupAiMessage: (agentType: string, message: string, lookupEntities: { objectType: string; objectId: string }[], isHeader?: boolean, headerText?: string) => Promise<void>;
  handleBasicAiMessage: (agentType: string, prompt: string, systemContext: string, isHeader: boolean, headerText: string) => Promise<void>;
  selectedObjects: SelectedItem[];
  setSelectedObjects: React.Dispatch<React.SetStateAction<SelectedItem[]>>;
  showAi: boolean;
  setShowAi: (show: boolean) => void;
  activeAgentType: string;
  setActiveAgentType: (agentType: string) => void;
  setAgentContext: (agentType: string, contextMessage: string) => void;
}

const AiContext = createContext<AiContextProps | undefined>(undefined);

/** Strips backend-injected metadata lines (keysUsed, sourcesUsed, etc.) from AI response text. */ // v2
const sanitizeResponseText = (text: string): string => {
  const lines = text.split('\n');
  const kept: string[] = [];
  let skippingMetadataBlock = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const startsMetadataBlock = /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

    if (startsMetadataBlock) {
      skippingMetadataBlock = true;
      continue;
    }

    if (skippingMetadataBlock) {
      const isContinuation =
        trimmed.length === 0 ||
        /^[-*]/.test(trimmed) ||
        /^[\[{\"]/.test(trimmed) ||
        /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

      if (isContinuation) {
        continue;
      }

      skippingMetadataBlock = false;
    }

    kept.push(line);
  }

  return kept.join('\n').trimEnd();
};

export const AiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [ agents, setAgents ]                   = useState<Record<string, AgentChatState>>( {} ),
        [ showAi, setShowAi ]                   = useState<boolean>( false ),
        [ activeAgentType, setActiveAgentType ] = useState<string>('activity'),
        [ selectedObjects, setSelectedObjects ] = useState<SelectedItem[]>( [] );
  const location = useLocation();
  const isOnAiPage = location.pathname.startsWith('/ai-assistant');


  const { GetAIActivityAgentResponse, GetAIResponse, GetAIProjectAgentResponse, GetAgenticAIAgentResponse, GetAIAgentResponse, FullAISearchResponse } = useCentralCalls();

  const setAgentContext = (agentType: string, contextMessage: string) => {
    updateAgent(agentType, { contextMessage });
  };
  const updateAgent = (agentType: string, data: Partial<AgentChatState>) => {
    setAgents(prev => ({
      ...prev,
      [agentType]: {
        ...prev[agentType],
        ...data
      }
    }));
  };

  const getAgent = (agentType: string): AgentChatState =>
    agents[agentType] || { messages: [], threadId: null };

  const handleBasicAiMessage = async (
    agentType: string,
    prompt: string,
    systemContext: string,
    isHeader: boolean = false,
    headerText: string = ''
  ): Promise<void> => {
    const timestamp = new Date();
    if (!isOnAiPage) setShowAi(true);
    setActiveAgentType(agentType);
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: prompt,
      isUser: true,
      timestamp,
      isHeader,
      headerText
    };

    const agentState = getAgent(agentType);
    const prevMessages = agentState.messages || [];
    const updatedMessages = [...prevMessages, userMessage];

    updateAgent(agentType, { messages: updatedMessages, isLoading: true });

    const chatHistory: ChatMessageBasic[] = prevMessages.map(msg => ({
      Role: msg.isHeader ? 'divider' : msg.isUser ? 'user' : 'assistant',
      Content: msg.isHeader ? msg.headerText || '' : msg.text,
      History: []
    }));

    chatHistory.push({ Role: 'user', Content: prompt, History: [] });

    try {
      const response = await GetAIResponse(prompt, systemContext, chatHistory);

      const aiMessage: ChatMessage = {
        id: crypto.randomUUID(),
        text: sanitizeResponseText(response.response || 'No response'),
        isUser: false,
        timestamp: new Date()
      };

      updateAgent(agentType, { messages: [...updatedMessages, aiMessage], isLoading: false });
    } catch (err) {
      console.error(err);
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        text: 'Error talking to AI agent.',
        isUser: false,
        timestamp: new Date()
      };
      updateAgent(agentType, { messages: [...updatedMessages, errorMessage], isLoading: false });
    }
  };

const handleSendMessage = async (
  agentType: string,
  userText: string,
  isHeader: boolean = false,
  headerText: string = '',
  bryntumJson?: any
) => {
  const agentState = getAgent(agentType);
  const contextMessage = agentState.contextMessage ? `${agentState.contextMessage}\n\n` : '';
  const finalMessage = `${contextMessage}${userText}`;

  const timestamp = new Date();
  if (!isOnAiPage) setShowAi(true);
  setActiveAgentType(agentType);
  const userMessage: ChatMessage = {
    id: crypto.randomUUID(),
    text: userText,
    isUser: true,
    timestamp,
    isHeader,
    headerText
  };

  const prevMessages = agentState.messages || [];
  const updatedUserMessages = [...prevMessages, userMessage];
  updateAgent(agentType, { messages: updatedUserMessages, isLoading: true });

  try {
    let response;
    if (agentType === 'activity') {
      response = await GetAIAgentResponse('activity', finalMessage, agentState.threadId);
    } else if (agentType === 'project') {
      response = await GetAIProjectAgentResponse(finalMessage, agentState.threadId);
    } else if (agentType === 'assistant') {
      response = await GetAgenticAIAgentResponse(finalMessage, agentState.threadId, bryntumJson);
    }
    if (response.threadId && response.threadId !== agentState.threadId) {
      updateAgent(agentType, { threadId: response.threadId });
      sessionStorage.setItem(`ai-thread-id-${agentType}`, response.threadId);
    }

    const aiMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: sanitizeResponseText(response.response || 'No response from AI.'),
      isUser: false,
      timestamp: new Date(),
      lookupDocs: response.lookupDocs || []
    };

    updateAgent(agentType, { messages: [...updatedUserMessages, aiMessage] });
  } catch (err) {
    console.error(err);
    const errorMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: 'Error talking to AI agent.',
      isUser: false,
      timestamp: new Date()
    };
    updateAgent(agentType, { messages: [...updatedUserMessages, errorMessage] });
  } finally {
    updateAgent(agentType, { isLoading: false });
  }
};

const handleSendLookupAiMessage = async (
  agentType: string,
  userText: string,
  lookupEntities: { objectType: string; objectId: string }[],
  isHeader: boolean = false,
  headerText: string = ''
) => {
  const agentState = getAgent(agentType);
  const resolvedAgentName = agentType === 'project' || agentType === 'activity' ? agentType : 'activity';
  const contextMessage = agentState.contextMessage ? `${agentState.contextMessage}\n\n` : '';
  const finalMessage = `${contextMessage}${userText}`;

  const timestamp = new Date();
  if (!isOnAiPage) setShowAi(true);
  setActiveAgentType(agentType);
  const userMessage: ChatMessage = {
    id: crypto.randomUUID(),
    text: userText,
    isUser: true,
    timestamp,
    isHeader,
    headerText
  };

  const prevMessages = agentState.messages || [];
  const updatedUserMessages = [...prevMessages, userMessage];
  updateAgent(agentType, { messages: updatedUserMessages, isLoading: true });

  try {
    const response = await FullAISearchResponse(
      resolvedAgentName,
      finalMessage,
      lookupEntities,
      agentState.threadId
    );

    const lookupDocs =
      response?.lookupDocs ??
      response?.LookupDocs ??
      response?.lookup_docs ??
      response?.Lookup_Docs ??
      [];

    if (response.threadId && response.threadId !== agentState.threadId) {
      updateAgent(agentType, { threadId: response.threadId });
      sessionStorage.setItem(`ai-thread-id-${agentType}`, response.threadId);
    }

    const aiMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: sanitizeResponseText(response.response || 'No response from AI.'),
      isUser: false,
      timestamp: new Date(),
      lookupDocs: Array.isArray(lookupDocs) ? lookupDocs : [],
      sourceIndexes: Array.isArray(response?.sourceIndexes) ? response.sourceIndexes : [],
      selectedIndexes: Array.isArray(response?.selectedIndexes) ? response.selectedIndexes : [],
      recordsByIndex: response?.recordsByIndex && typeof response.recordsByIndex === 'object'
        ? response.recordsByIndex
        : {},
      conversationId: typeof response?.conversationId === 'string' ? response.conversationId : undefined,
      reusedDataset: typeof response?.reusedDataset === 'boolean' ? response.reusedDataset : undefined,
      threadId: typeof response?.threadId === 'string' ? response.threadId : undefined,
      debug: response?.debug
    };

    updateAgent(agentType, { messages: [...updatedUserMessages, aiMessage] });
  } catch (err) {
    console.error(err);
    const errorObj = err as any;
    const apiError = errorObj?.apiError;
    const aiAssistantDebugRaw = apiError?.details?.aiAssistantDebug;
    let aiAssistantDebugParsed = aiAssistantDebugRaw;

    if (typeof aiAssistantDebugRaw === 'string') {
      try {
        aiAssistantDebugParsed = JSON.parse(aiAssistantDebugRaw);
      } catch {
        aiAssistantDebugParsed = aiAssistantDebugRaw;
      }
    }

    const errorText = apiError?.message || errorObj?.message || 'Error talking to AI agent.';
    const errorMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: errorText,
      isUser: false,
      timestamp: new Date(),
      isError: true,
      debug: {
        status: errorObj?.status,
        statusText: errorObj?.statusText,
        error: apiError?.error,
        details: apiError?.details,
        aiAssistantDebug: aiAssistantDebugParsed,
      }
    };
    updateAgent(agentType, { messages: [...updatedUserMessages, errorMessage] });
  } finally {
    updateAgent(agentType, { isLoading: false });
  }
};

  return (
    <AiContext.Provider
      value={{
        agents,
        updateAgent,
        getAgent,
        handleSendMessage,
        handleSendLookupAiMessage,
        handleBasicAiMessage,
        selectedObjects,
        setSelectedObjects,
        showAi,
        setShowAi,
        activeAgentType,
        setActiveAgentType,
        setAgentContext
      }}
    >
      {children}
    </AiContext.Provider>
  );
};

export const useAiContext = () => {
  const context = useContext(AiContext);
  if (!context) throw new Error('useAiContext must be used within an AiProvider');
  return context;
};